"use client";

import { c as _c } from "react/compiler-runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Button from "@/components/ui/button";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Input from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import createId from "@/lib/create-id";
import { toast } from "@/lib/use-toast";
import type { ExplicitMeasurementGoal, MeasurementField } from "@/types/measurement";
import { useDeleteMeasurementGoalMutation, useSetMeasurementGoalMutation } from "@/query/patient/measurement-goals";
type GoalMode = "single" | "range";
const goalSchema = z.discriminatedUnion("mode", [z.object({
  mode: z.literal("single"),
  goalValue: z.string().min(1, "Target value is required").refine(v => !isNaN(parseFloat(v)), "Must be a number"),
  goalMin: z.string().optional(),
  goalMax: z.string().optional(),
  label: z.string().optional()
}), z.object({
  mode: z.literal("range"),
  goalValue: z.string().optional(),
  goalMin: z.string().min(1, "Min is required").refine(v => !isNaN(parseFloat(v)), "Must be a number"),
  goalMax: z.string().min(1, "Max is required").refine(v => !isNaN(parseFloat(v)), "Must be a number"),
  label: z.string().optional()
}).refine(d => parseFloat(d.goalMin) < parseFloat(d.goalMax), {
  message: "Min must be less than max",
  path: ["goalMax"]
})]);
type GoalFormValues = z.infer<typeof goalSchema>;
interface MeasurementGoalDialogProps {
  patientId: string;
  field: MeasurementField;
  existingGoal?: ExplicitMeasurementGoal | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
export default function MeasurementGoalDialog(t0) {
  const $ = _c(88);
  const {
    patientId,
    field,
    existingGoal,
    open,
    onOpenChange
  } = t0;
  const setMutation = useSetMeasurementGoalMutation(patientId);
  const deleteMutation = useDeleteMeasurementGoalMutation(patientId);
  const hasRange = existingGoal?.goalMin != null || existingGoal?.goalMax != null;
  const [mode, setMode] = useState(hasRange ? "range" : "single");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = zodResolver(goalSchema);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = hasRange ? "range" : "single";
  let t3;
  if ($[1] !== existingGoal?.goalValue) {
    t3 = existingGoal?.goalValue?.toString() ?? "";
    $[1] = existingGoal?.goalValue;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== existingGoal?.goalMin) {
    t4 = existingGoal?.goalMin?.toString() ?? "";
    $[3] = existingGoal?.goalMin;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== existingGoal?.goalMax) {
    t5 = existingGoal?.goalMax?.toString() ?? "";
    $[5] = existingGoal?.goalMax;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = existingGoal?.label ?? "";
  let t7;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t4 || $[10] !== t5 || $[11] !== t6) {
    t7 = {
      resolver: t1,
      defaultValues: {
        mode: t2,
        goalValue: t3,
        goalMin: t4,
        goalMax: t5,
        label: t6
      }
    };
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: t8
  } = useForm(t7);
  const {
    errors
  } = t8;
  let t9;
  if ($[13] !== existingGoal?.goalMax || $[14] !== existingGoal?.goalMin || $[15] !== existingGoal?.goalValue || $[16] !== existingGoal?.label || $[17] !== open || $[18] !== reset) {
    t9 = () => {
      if (open) {
        const hasRng = existingGoal?.goalMin != null || existingGoal?.goalMax != null;
        const newMode = hasRng ? "range" : "single";
        setMode(newMode);
        reset({
          mode: newMode,
          goalValue: existingGoal?.goalValue?.toString() ?? "",
          goalMin: existingGoal?.goalMin?.toString() ?? "",
          goalMax: existingGoal?.goalMax?.toString() ?? "",
          label: existingGoal?.label ?? ""
        });
      }
    };
    $[13] = existingGoal?.goalMax;
    $[14] = existingGoal?.goalMin;
    $[15] = existingGoal?.goalValue;
    $[16] = existingGoal?.label;
    $[17] = open;
    $[18] = reset;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== existingGoal || $[21] !== open || $[22] !== reset) {
    t10 = [open, existingGoal, reset];
    $[20] = existingGoal;
    $[21] = open;
    $[22] = reset;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  useEffect(t9, t10);
  let t11;
  if ($[24] !== field.id || $[25] !== field.name || $[26] !== mode || $[27] !== onOpenChange || $[28] !== patientId || $[29] !== setMutation) {
    t11 = async data => {
      const payload = mode === "single" ? {
        id: createId("mgl"),
        patientId,
        fieldId: field.id,
        goalValue: data.goalValue ? parseFloat(data.goalValue) : undefined,
        label: data.label || undefined
      } : {
        id: createId("mgl"),
        patientId,
        fieldId: field.id,
        goalMin: data.goalMin ? parseFloat(data.goalMin) : undefined,
        goalMax: data.goalMax ? parseFloat(data.goalMax) : undefined,
        label: data.label || undefined
      };
      await setMutation.mutateAsync(payload);
      toast({
        title: "Goal set",
        description: `Goal for ${field.name} has been saved`
      });
      onOpenChange(false);
    };
    $[24] = field.id;
    $[25] = field.name;
    $[26] = mode;
    $[27] = onOpenChange;
    $[28] = patientId;
    $[29] = setMutation;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== handleSubmit || $[32] !== t11) {
    t12 = handleSubmit(t11);
    $[31] = handleSubmit;
    $[32] = t11;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const onSubmit = t12;
  let t13;
  if ($[34] !== deleteMutation || $[35] !== existingGoal || $[36] !== field.name || $[37] !== onOpenChange) {
    t13 = async () => {
      if (!existingGoal) {
        return;
      }
      await deleteMutation.mutateAsync(existingGoal.id);
      toast({
        title: "Goal removed",
        description: `Goal for ${field.name} has been removed`
      });
      onOpenChange(false);
    };
    $[34] = deleteMutation;
    $[35] = existingGoal;
    $[36] = field.name;
    $[37] = onOpenChange;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  const handleRemove = t13;
  let t14;
  if ($[39] !== field.unit) {
    t14 = field.unit && <span className="ml-1 font-normal text-foreground-secondary">({field.unit})</span>;
    $[39] = field.unit;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== field.name || $[42] !== t14) {
    t15 = <DialogHeader><DialogTitle>Set Goal — {field.name}{t14}</DialogTitle></DialogHeader>;
    $[41] = field.name;
    $[42] = t14;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Label>Goal Type</Label>;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== setValue) {
    t17 = v => {
      setMode(v as GoalMode);
      setValue("mode", v as GoalMode);
    };
    $[45] = setValue;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <label className="flex items-center gap-2 text-body"><RadioGroupItem value="single" />Single Value</label>;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <label className="flex items-center gap-2 text-body"><RadioGroupItem value="range" />Range</label>;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== mode || $[50] !== t17) {
    t20 = <div className="space-y-1">{t16}<RadioGroup value={mode} onValueChange={t17} className="flex gap-4">{t18}{t19}</RadioGroup></div>;
    $[49] = mode;
    $[50] = t17;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== errors.goalMax || $[53] !== errors.goalMin || $[54] !== errors.goalValue || $[55] !== mode || $[56] !== register) {
    t21 = mode === "single" ? <div className="space-y-1"><Label>Target Value</Label><Input type="number" step="any" placeholder="e.g. 165" data-testid="goal-value-input" hasError={!!errors.goalValue} errorMessage={errors.goalValue?.message} {...register("goalValue")} /></div> : <div className="flex gap-3"><div className="flex-1 space-y-1"><Label>Min</Label><Input type="number" step="any" placeholder="e.g. 110" data-testid="goal-min-input" hasError={!!errors.goalMin} errorMessage={errors.goalMin?.message} {...register("goalMin")} /></div><div className="flex-1 space-y-1"><Label>Max</Label><Input type="number" step="any" placeholder="e.g. 130" data-testid="goal-max-input" hasError={!!errors.goalMax} errorMessage={errors.goalMax?.message} {...register("goalMax")} /></div></div>;
    $[52] = errors.goalMax;
    $[53] = errors.goalMin;
    $[54] = errors.goalValue;
    $[55] = mode;
    $[56] = register;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Label>Label</Label>;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  let t23;
  if ($[59] !== register) {
    t23 = register("label");
    $[59] = register;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  let t24;
  if ($[61] !== t23) {
    t24 = <div className="space-y-1">{t22}<Input type="text" placeholder="Goal" {...t23} /></div>;
    $[61] = t23;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  let t25;
  if ($[63] !== onSubmit || $[64] !== t20 || $[65] !== t21 || $[66] !== t24) {
    t25 = <DialogBody><form id="measurement-goal-form" onSubmit={onSubmit} className="space-y-4">{t20}{t21}{t24}</form></DialogBody>;
    $[63] = onSubmit;
    $[64] = t20;
    $[65] = t21;
    $[66] = t24;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] !== deleteMutation || $[69] !== existingGoal || $[70] !== handleRemove) {
    t26 = existingGoal && <Button variant="ghost" size="sm" onClick={handleRemove} isLoading={deleteMutation.isPending} className="mr-auto text-status-error-text" data-testid="goal-remove-button">Remove Goal</Button>;
    $[68] = deleteMutation;
    $[69] = existingGoal;
    $[70] = handleRemove;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  let t27;
  if ($[72] !== onOpenChange) {
    t27 = <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>;
    $[72] = onOpenChange;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  let t28;
  if ($[74] !== setMutation.isPending) {
    t28 = <Button type="submit" form="measurement-goal-form" size="sm" isLoading={setMutation.isPending} data-testid="goal-save-button">Save</Button>;
    $[74] = setMutation.isPending;
    $[75] = t28;
  } else {
    t28 = $[75];
  }
  let t29;
  if ($[76] !== t26 || $[77] !== t27 || $[78] !== t28) {
    t29 = <DialogFooter>{t26}{t27}{t28}</DialogFooter>;
    $[76] = t26;
    $[77] = t27;
    $[78] = t28;
    $[79] = t29;
  } else {
    t29 = $[79];
  }
  let t30;
  if ($[80] !== t15 || $[81] !== t25 || $[82] !== t29) {
    t30 = <DialogContent className="max-w-sm">{t15}{t25}{t29}</DialogContent>;
    $[80] = t15;
    $[81] = t25;
    $[82] = t29;
    $[83] = t30;
  } else {
    t30 = $[83];
  }
  let t31;
  if ($[84] !== onOpenChange || $[85] !== open || $[86] !== t30) {
    t31 = <Dialog open={open} onOpenChange={onOpenChange}>{t30}</Dialog>;
    $[84] = onOpenChange;
    $[85] = open;
    $[86] = t30;
    $[87] = t31;
  } else {
    t31 = $[87];
  }
  return t31;
}